<template>
  <div class="flex flex-col overflow-y-auto grow">
    <ChooseReaderModal />
    <div class="pt-2 py-1 px-2 lg:px-4 lg:shadow-lg relative z-30 bg-white" ref="formHeader">
      <div class="flex">
        <PBreadcrumb
          :parts="parts"
          :current-page-label="isDraft ? 'Ajout de parapheur' : 'Edition de parapheur'"
          class="mb-1 hidden lg:block"
        />
        <div class="relative h-0 ml-auto">
          <div class="absolute right-0 flex gap-1">
            <SaveStatus class="hidden lg:flex" :loading="asyncOperation.loading.value" />
          </div>
        </div>
      </div>
      <div class="flex items-center mb-1 lg:mb-0">
        <LetterFileNameInput
          data-sentry-id="LetterFileNameInput"
          class="mb-1v"
          v-model="name"
          :error="nameErrors.length > 0"
          :readonly="isCurrent"
          @submit="saveName"
        />
      </div>
      <div class="flex items-center flex-wrap">
        <p class="text-grey-425 h-auto md:h-4 mr-2">
          <span v-if="isDraft">En cours de création • </span>
          Numéro Chrono {{ letterFile ? letterFile.chronoNumber : "" }}
        </p>
        <div
          class="xl:ml-2 mb-1 flex flex-col lg:flex-row gap-1 lg:items-center flex-wrap order-first lg:order-last"
          v-if="letterFile"
        >
          <div class="flex items-center gap-1">
            <PButton
              data-sentry-id="ToggleAdvancedOptions"
              mode="tertiary"
              class="font-bold"
              @click="toggleAdvanced"
              :aria-expanded="advanced"
            >
              Options avancées
              <OhVueIcon :name="advanced ? arrowUpIcon : arrowDownIcon" class="ml-1v" />
            </PButton>
            <PTooltip label="Copier le lien" :delay="500" :offset="10">
              <PButton
                data-sentry-id="CopyLinkBtn"
                size="icon"
                mode="tertiary-borderless"
                aria-label="Copier l'URL"
                @click="copyUrl"
              >
                <OhVueIcon :name="linkIcon" scale="1.5" />
              </PButton>
            </PTooltip>
            <div class="hidden lg:block">
              <History :id="letterFile.id" />
            </div>
          </div>
          <div class="flex flex-wrap gap-1 xl:ml-2">
            <div v-for="tag in letterFile.tags" :key="tag.id">
              <PBadge type="error">
                {{ tag.name }}
              </PBadge>
            </div>

            <DueInBadge :letter-file="letterFile" />
          </div>
        </div>
      </div>
      <div v-show="advanced" class="bg-blue-france-975 px-2 mt-1 lg:mt-0 lg:px-4 pt-2">
        <div class="flex items-center gap-1 lg:gap-2 flex-wrap">
          <div class="w-full md:w-auto">
            <AutoSuggester
              data-sentry-id="LetterFileManagerInput"
              :model-value="manager"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.manager, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.manager, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.manager)"
              placeholder="Saisir"
              label="Responsable du parapheur"
              :disabled="isCurrent"
              aria-label="Saisir un responsable du parapheur"
              class="text-sm w-full"
            />
          </div>
          <div class="w-full md:w-auto">
            <PDateTimePicker
              data-sentry-id="LetterFileDeadlineInput"
              label="Date limite de traitement"
              :model-value="deadline"
              @update:model-value="saveDeadline"
              :disabled="isCurrent"
              aria-label="Saisir une date limite de traitement: format jj/mm/aaaa"
              class="text-sm"
              hideMessages
            />
          </div>
          <div class="order-first lg:order-last">
            <LetterFileTagsInput
              data-sentry-id="LetterFileTagsInput"
              class="flex-wrap lg:justify-evenly lg:pt-4"
              v-if="letterFile"
              :letter-file="letterFile"
              :readonly="isCurrent"
              @input="saveTags"
            />
          </div>
        </div>
        <div class="pt-2 lg:pt-0 pb-2 lg:pb-3 lg:mt-2">
          <div class="underline mb-1" id="external-ref">Référence externe</div>
          <div
            class="flex items-center text-sm gap-2 flex-col md:flex-row [&_label_span]:w-full [&>*]:w-full md:[&>*]:w-auto"
            :class="forceAlignCss"
            role="group"
            aria-labelledby="external-ref"
          >
            <AutoSuggester
              data-sentry-id="LetterFileSenderOrganizationInput"
              :model-value="senderOrganization"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.senderOrganization, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.senderOrganization, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.senderOrganization)"
              placeholder="Saisir"
              label="Organisme"
              :disabled="isCurrent"
              aria-label="Saisir un organisme"
            />
            <AutoSuggester
              data-sentry-id="LetterFileSenderNameInput"
              :model-value="senderName"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.senderName, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.senderName, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.senderName)"
              placeholder="Saisir"
              label="Nom, prénom"
              :disabled="isCurrent"
              aria-label="Saisir un nom et prénom"
            />
            <AutoSuggester
              data-sentry-id="LetterFileSenderRoleInput"
              :model-value="senderRole"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.senderRole, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.senderRole, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.senderRole)"
              placeholder="Saisir"
              label="Fonction"
              :disabled="isCurrent"
              aria-label="Saisir une fonction"
            />
            <AutoSuggester
              data-sentry-id="LetterFileMailTypeInput"
              :model-value="mailType"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.mailType, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.mailType, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.mailType)"
              placeholder="Saisir"
              label="Type de courrier"
              :disabled="isCurrent"
              aria-label="Saisir un type de courrier"
            />
            <AutoSuggester
              data-sentry-id="LetterFileExternalMailNumberInput"
              :model-value="externalMailNumber"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.externalMailNumber, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.externalMailNumber, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.externalMailNumber)"
              placeholder="Saisir"
              label="Numéro du courrier"
              :disabled="isCurrent"
              aria-label="Saisir un numéro de courrier extérieur"
            />
            <div>
              <PDateTimePicker
                data-sentry-id="LetterFileExternalDateInput"
                label="Date du courrier extérieur"
                :model-value="arrivalDate"
                @update:model-value="saveArrivalDate"
                :disabled="isCurrent"
                aria-label="Saisir une date de courrier extérieur: format jj/mm/aaaa"
                hideMessages
              />
            </div>
            <AutoSuggester
              :model-value="recipientName"
              @update:model-value="saveSimpleField(LetterFileSuggestableField.recipientName, $event)"
              @remove="removeSuggestion(LetterFileSuggestableField.recipientName, $event)"
              :loading="!suggestionStore.loaded"
              :suggestions="getSuggestableFieldValues(LetterFileSuggestableField.recipientName)"
              placeholder="Saisir"
              label="Destinataire du courrier"
              :disabled="isCurrent"
              aria-label="Saisir un destinataire du courrier reçu"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="lg:hidden pt-5">
      <PTabs
        v-model="tab"
        customBtnStyle
        :class="`[&_.p-tab]:p-0! [&_.p-tabs]:border-0 [&_.p-tag-button]:border-transparent [&_.p-tag-button-active]:bg-white [&_.p-tag-button-active]:border-grey-925 ${pTabColors.bg} ${pTabColors.borderTop}`"
      >
        <PTab name="flux" label="Flux">
          <Flux
            :letter-file="letterFile"
            :loading="loadingStore.isLoading('set-lf')"
            :readonly="isCurrent"
            @undraft="undraft"
            class="w-full"
          />
        </PTab>
        <PTab name="comments" label="Commentaires">
          <Comments :letter-file="letterFile" :readonly="isCurrent" class="w-full z-10" />
        </PTab>
        <PTab name="documents" label="Documents">
          <Documents :letter-file="letterFile" :readonly="isCurrent" class="w-full" />
        </PTab>
      </PTabs>
    </div>
    <div class="hidden lg:flex flex-col lg:flex-row lg:h-0 lg:grow">
      <Flux :readonly="isCurrent" :loading="loadingStore.isLoading('set-lf')" @undraft="undraft" class="w-5/12" />
      <Comments :readonly="isCurrent" class="z-10 w-3/12" />
      <Documents :readonly="isCurrent" class="w-4/12" />
    </div>
  </div>
</template>

<script setup lang="ts">
import syncCurrentLetterFile from "@/app/composable/sync-current-letter-file.ts";
import {
  AlertDuration,
  AlertType,
  PBadge,
  PBreadcrumb,
  PButton,
  PDateTimePicker,
  PTab,
  PTabs,
  PTooltip,
  timeout,
  useAlertManager,
  useIcons,
  usePastaRouter,
  usePastaValidation,
} from "@pasta/box-v2";
import { InvalidTokenError } from "@pasta/front-auth-v2";
import { useSentry } from "@pasta/front-sentry-v2";
import { useHead } from "@unhead/vue";
import { required } from "@vuelidate/validators";
import { useEventListener, useWindowFocus } from "@vueuse/core";
import { isNil } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { RiArrowDropDownLine, RiArrowDropUpLine, RiLinksLine } from "oh-vue-icons/icons";
import { computed, onMounted, provide, ref, watch } from "vue";
import AutoSuggester from "../../components/AutoSuggester.vue";
import ChooseReaderModal from "../../components/ChooseReaderModal.vue";
import DueInBadge from "../../components/DueInBadge.vue";
import History from "../../components/History.vue";
import LetterFileNameInput from "../../components/LetterFileNameInput.vue";
import LetterFileTagsInput from "../../components/LetterFileTagsInput.vue";
import { useAsyncOperation } from "../../composable/useAsyncOperation";
import { useLetterFileColor } from "../../composable/useLetterFileColor";
import { useLetterFileEvents } from "../../composable/useLetterFileEvents";
import { useShowSignedNotified } from "../../composable/useShowSignedNotified";
import { LetterFileStatus } from "../../dto/letter-file.dto";
import { LetterFileSuggestableField } from "../../dto/suggestion.dto";
import { useLetterFileStore } from "../../stores/letter-file.store";
import { useLoadingStore } from "../../stores/loading.store";
import { useMassSignatureStore } from "../../stores/mass-signature.store";
import { useSuggestionStore } from "../../stores/suggestion.store";
import { FormOperation } from "../../types";
import Comments from "./Comments.vue";
import Documents from "./Documents/Documents.vue";
import Flux from "./Flux/Flux.vue";
import SaveStatus from "./SaveStatus.vue";
import { type FormProvide, FormToken, wrapUnlessSessionExpired } from "./utils";

const forceAlignCss = [
  "md:[&_label>div:nth-of-type(1)]:whitespace-nowrap",
  "md:[&_label>div:nth-of-type(1)]:overflow-hidden",
  "md:[&_label>div:nth-of-type(1)]:text-ellipsis",
  "md:[&_label>div:nth-of-type(1)]:max-w-[14vw]",
].join(" ");

const isDraft = computed(() => letterFile.value?.draft);

const parts = [{ label: "Accueil", to: "/" }];
const [arrowDownIcon, arrowUpIcon, linkIcon] = useIcons(RiArrowDropDownLine, RiArrowDropUpLine, RiLinksLine);

const letterFileStore = useLetterFileStore();
const letterFile = computed(() => letterFileStore.getCurrent());
const alertManager = useAlertManager();
const router = usePastaRouter();
const suggestionStore = useSuggestionStore();
const { pTabColors } = useLetterFileColor();
const loadingStore = useLoadingStore();

const advanced = ref<boolean>(false);
const deadline = ref<string>("");
const arrivalDate = ref<string>("");
const tab = ref<string>("flux");

useHead({
  title: () => `Parapheur n°${letterFile.value?.chronoNumber}`,
})!;

syncCurrentLetterFile("form-update");
useLetterFileEvents(refresh);

const sentry = useSentry();
const windowFocused = useWindowFocus();
watch(
  () => windowFocused.value,
  async value => {
    if (value) {
      try {
        await timeout(100); // fixes coming back from confirm upon delete, thus fetching a deleted letter file
        await refresh(false);
      } catch (e) {
        if (e instanceof InvalidTokenError) {
          throw e;
        }

        sentry.captureException(e as Error);
        alertManager.createAlert({
          type: AlertType.WARNING,
          duration: AlertDuration.MEDIUM,
          message:
            "Nous n'avons pas pu nous connecter au serveur du Parapheur, veuillez vérifier votre configuration réseau.",
        });
      }
    }
  }
);

async function toggleAdvanced() {
  advanced.value = !advanced.value;
  if (!suggestionStore.loaded) {
    await suggestionStore.getMy();
  }
}

function getSuggestableFieldValues(field: LetterFileSuggestableField) {
  return [...(suggestionStore.map.get(field)?.values || [])].map(val => ({
    label: val,
    value: val,
  }));
}

async function saveTags(tags: number[]) {
  await asyncOperation.notifyAsyncOperation(async () => {
    await letterFileStore.update(letterFile.value!.id, { tags });
    await refresh();
  }, FormOperation.TAGS);
}

async function saveDeadline(value: string | string[]) {
  if (Array.isArray(value)) return;
  deadline.value = value;
  if (letterFile.value!.deadline !== value) {
    await asyncOperation.notifyAsyncOperation(async () => {
      await letterFileStore.update(letterFile.value!.id, {
        deadline: deadline.value || null,
      });
      await refresh();
    }, FormOperation.DEADLINE);
  }
}

async function saveArrivalDate(value: string | string[]) {
  if (Array.isArray(value)) return;
  arrivalDate.value = value;
  if (letterFile.value!.arrivalDate !== value) {
    await asyncOperation.notifyAsyncOperation(async () => {
      await letterFileStore.update(letterFile.value!.id, {
        arrivalDate: arrivalDate.value || null,
      });
      await refresh();
    }, FormOperation.ARRIVAL_DATE);
  }
}

const externalMailNumber = ref<string>("");
const senderOrganization = ref<string>("");
const senderName = ref<string>("");
const senderRole = ref<string>("");
const mailType = ref<string>("");
const recipientName = ref<string>("");
const manager = ref<string>("");
const simpleInputsMap = new Map([
  ["externalMailNumber", externalMailNumber],
  ["senderOrganization", senderOrganization],
  ["senderName", senderName],
  ["senderRole", senderRole],
  ["mailType", mailType],
  ["recipientName", recipientName],
  ["manager", manager],
]);

async function saveSimpleField(fieldName: LetterFileSuggestableField, value: string) {
  simpleInputsMap.get(fieldName)!.value = value;
  if (
    letterFile.value![fieldName] !== value &&
    !(isNil(letterFile.value![fieldName]) && !value) // do not save change from null to ""
  ) {
    await asyncOperation.notifyAsyncOperation(async () => {
      await letterFileStore.update(letterFile.value!.id, {
        [fieldName]: value,
      });
      await refresh();
      const suggestions = getSuggestableFieldValues(fieldName).map(item => item.value);
      if (!suggestions.includes(value)) {
        await suggestionStore.getMy();
      }
    }, FormOperation[fieldName]);
  }
}

async function removeSuggestion(fieldName: LetterFileSuggestableField, value: string) {
  const values = (suggestionStore.map.get(fieldName)?.values || []).filter(val => val !== value);
  await suggestionStore.update(fieldName, values);
  await suggestionStore.getMy();
}

const validation = usePastaValidation();
const [name, nameErrors] = validation.ref<string>("", [
  { validator: required, message: "Le nom du parapheur est requis" },
]);

watch(
  () => letterFile.value,
  async () => {
    name.value = letterFile.value?.name || "";
    deadline.value = letterFile.value?.deadline || "";
    arrivalDate.value = letterFile.value?.arrivalDate || "";
    externalMailNumber.value = letterFile.value?.externalMailNumber || "";
    senderOrganization.value = letterFile.value?.senderOrganization || "";
    senderName.value = letterFile.value?.senderName || "";
    senderRole.value = letterFile.value?.senderRole || "";
    mailType.value = letterFile.value?.mailType || "";
    recipientName.value = letterFile.value?.recipientName || "";
    manager.value = letterFile.value?.manager || "";
  },
  { immediate: true }
);

async function saveName() {
  if (name.value !== letterFile.value!.name) {
    await asyncOperation.notifyAsyncOperation(async () => {
      await letterFileStore.update(letterFile.value!.id, { name: name.value });
      await refresh();
    }, FormOperation.LETTER_FILE_NAME);
  }
}

const asyncOperation = useAsyncOperation();
provide<FormProvide>(FormToken, { refresh, letterFile });

async function refresh(force = true) {
  await Promise.all([letterFileStore.fetchCurrent(force).catch(wrapUnlessSessionExpired), timeout(500)]);
}

const notifySigned = useShowSignedNotified();

async function undraft() {
  const isValid = await validation.validate();
  if (!isValid) return;

  const hasSigned = letterFile?.value?.current?.hasSigned;

  try {
    const next = letterFile.value?.downstream?.find(el => el.position === 1)?.name;
    const message = next ? `Envoyé à ${next}` : "Archivé";

    await asyncOperation.notifyAsyncOperation(async () => {
      await letterFileStore.undraft(letterFile.value!.id);
      await refresh();
    }, FormOperation.UNDRAFT);

    alertManager.createAlert({
      message,
      type: AlertType.SUCCESS,
      duration: AlertDuration.MEDIUM,
    });

    if (hasSigned) {
      notifySigned.register(letterFile.value!);
    }

    router.push("/");
  } catch {
    alertManager.createAlert({
      message: "L'opération a échoué",
      type: AlertType.ERROR,
      duration: AlertDuration.LONG,
    });
  } finally {
    validation.reset();
  }
}

const isCurrent = computed(() => {
  return letterFile.value?.status !== LetterFileStatus.CURRENT;
});

// prevent from dropping files on the page which would open them
useEventListener("drop", (event: DragEvent) => {
  event.preventDefault();
});
useEventListener("dragover", (event: DragEvent) => {
  event.preventDefault();
});

function copyUrl() {
  alertManager.createAlert({
    type: AlertType.INFO,
    duration: AlertDuration.MEDIUM,
    message: "Lien copié ! Seules les personnes déclarées sur le parapheur pourront s'y connecter.",
  });
  navigator.clipboard.writeText(window.location.toString());
}

const massSignatureStore = useMassSignatureStore();
onMounted(async () => {
  if (massSignatureStore.currentMassSignature?.creationDate) {
    router.push("/");
  }
});

const formHeader = ref<HTMLDivElement | null>(null);
</script>
